import { ArrowUpRight } from "lucide-react";
import { useDeck } from "../../lib/deck-context";
import { Card, Eyebrow, Reveal, Section, SectionHeader, StatusBadge } from "../deck/primitives";

const PHASES = [
  { t: "City Pathway", items: [["Complete ability-to-perform package", "preliminary"], ["Complete City Attorney / Surplus Land Act review", "preliminary"], ["City Council Closed Session / direction", "target"], ["Negotiate Exclusive Negotiating Agreement or equivalent", "target"]] },
  { t: "Partnership + Feasibility", items: [["Finalize Joint Venture structure", "target"], ["Engage architect / civil / General Contractor preconstruction", "target"], ["Validate tenant rents and leasing strategy", "tbv"], ["Update development budget", "target"]] },
  { t: "Agreements + Entitlement", items: [["Negotiate permanent City development / ground lease agreement", "target"], ["Advance entitlements", "target"]] },
  { t: "Capitalization + Construction", items: [["Secure debt + equity", "nyc"], ["Begin construction", "target"]] },
];

const QA = [
  ["What is the opportunity?", 0], ["Who owns the property?", 1], ["What rights does Arqitek currently have?", 2], ["Where are we with the City?", 2],
  ["What are we building?", 3], ["Is there market demand for this project?", 4], ["How much income could it generate?", 5], ["How did we calculate NOI?", 5], ["How much will it cost?", 6],
  ["How were those costs estimated?", 6], ["Why this capitalization rate?", 7], ["How was the valuation calculated?", 8], ["What if rents or cap rates move?", 8],
  ["How much debt and equity are required?", 9], ["What does each stage cost?", 10], ["What are all of our exit options?", 10], ["What could go wrong?", 12],
  ["What does Arqitek need from a JV partner?", 11],
];

export default function NextSteps() {
  const { go } = useDeck();
  let n = 0;
  return (
    <Section testid="section-next">
      <SectionHeader num="13" eyebrow="Next Steps" title="The path from opportunity to construction." lede="Twelve gated milestones. Each phase is re-budgeted and re-underwritten before advancing." />
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
        {PHASES.map((p, pi) => (
          <Reveal key={p.t} delay={pi * 0.08}>
            <Card className="p-7 h-full">
              <div className="font-mono text-[10px] tracking-[0.2em] text-arq">PHASE {pi + 1}</div>
              <div className="mt-2 font-display text-xl text-ink">{p.t}</div>
              <ol className="mt-6 space-y-5">
                {p.items.map(([t, s]) => {
                  n += 1;
                  return (
                    <li key={t} data-testid={`milestone-${n}`} className="flex gap-4">
                      <span className={`h-8 w-8 shrink-0 rounded-full grid place-items-center font-mono text-xs ${n <= 2 ? "bg-arq text-white" : "border border-line text-ink"}`}>{n}</span>
                      <div><div className="text-[14px] text-char leading-snug">{t}</div><div className="mt-2"><StatusBadge s={s} /></div></div>
                    </li>
                  );
                })}
              </ol>
            </Card>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2} className="mt-14">
        <div className="flex items-end justify-between gap-6 mb-5">
          <div><Eyebrow>Partner Diligence Index</Eyebrow><div className="mt-2 font-display text-2xl text-ink">Every question, answered in the deck.</div></div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line rounded-[14px] overflow-hidden border border-line">
          {QA.map(([q, i]) => (
            <button key={q} data-testid={`qa-link-${q.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 36)}`} onClick={() => go(i)} className="group bg-white px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-arq-soft transition-colors duration-200">
              <span className="text-[14px] text-char">{q}</span>
              <span className="flex items-center gap-1 font-mono text-[11px] text-steel group-hover:text-arq">{String(i + 1).padStart(2, "0")}<ArrowUpRight size={13} /></span>
            </button>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.25}>
        <div data-testid="closing-statement" className="mt-10 rounded-[18px] bg-ink-deep text-white p-10 lg:p-14">
          <div className="font-mono text-[11px] tracking-[0.4em] text-arq-mid">ARQITEK</div>
          <p className="mt-6 font-display text-3xl lg:text-4xl leading-[1.25] max-w-4xl">
            Where the deal stands, how the numbers were built, what still needs to be proven — and where we can make money together.
          </p>
          <div className="mt-8 text-sm text-white/60">Prepared by Arqitek LLC · October 2026 · Preliminary & Confidential</div>
        </div>
      </Reveal>
    </Section>
  );
}
